/**
 * Cross-Tab Synchronization Helper
 *
 * Added based on Dinisuru's request:
 * In multi-tab browsing, changing companies or logging out in one tab would previously
 * leave other tabs desynchronized, creating a risk of posting transactions into the wrong company.
 * This helper uses the native browser BroadcastChannel API to broadcast context changes across all
 * open tabs under the same origin without using localStorage.
 */

const CHANNEL_NAME = 'myledger_cross_tab_sync';

export type CrossTabMessage =
  | {
      type: 'COMPANY_CHANGED';
      companyId: string;
      companyCode: string;
      companyName: string;
      timestamp: number;
    }
  | {
      type: 'AUTH_LOGOUT';
      timestamp: number;
    };

let broadcastChannel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
    return null;
  }
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
  }
  return broadcastChannel;
}

/** Broadcast that the active company was changed */
export function broadcastCompanyChange(companyId: string, companyCode: string, companyName: string): void {
  try {
    const ch = getChannel();
    if (ch) {
      ch.postMessage({
        type: 'COMPANY_CHANGED',
        companyId,
        companyCode,
        companyName,
        timestamp: Date.now(),
      } as CrossTabMessage);
    }
  } catch (err) {
    console.warn('[CrossTabSync] Failed to broadcast company change:', err);
  }
}

/** Broadcast that the user logged out */
export function broadcastLogout(): void {
  try {
    const ch = getChannel();
    if (ch) {
      ch.postMessage({
        type: 'AUTH_LOGOUT',
        timestamp: Date.now(),
      } as CrossTabMessage);
    }
  } catch (err) {
    console.warn('[CrossTabSync] Failed to broadcast logout:', err);
  }
}

/** Listen for cross-tab messages */
export function listenCrossTabMessages(onMessage: (msg: CrossTabMessage) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};

  const handler = (event: MessageEvent<CrossTabMessage>) => {
    if (event?.data && typeof event.data === 'object' && 'type' in event.data) {
      onMessage(event.data);
    }
  };

  ch.addEventListener('message', handler);
  return () => {
    ch.removeEventListener('message', handler);
  };
}
